import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Icon from '../../components/icons.jsx'
import { useApp } from '../../context/AppContext.jsx'
import { useAuth } from '../../context/AuthContext.jsx'
import * as api from '../../db/api.js'
import ProfileAccountDetails from './ProfileAccountDetails.jsx'
import './meu-perfil.css'

/**
 * Meu perfil — tela inicial da área de Configurações.
 *
 * Focada apenas nas informações pessoais do usuário: foto, nome, e-mail,
 * salvar alterações, sair e excluir a conta. As demais seções que antes
 * conviviam aqui (clientes, conexões, convites) foram movidas para as telas
 * dedicadas da área de Configurações.
 *
 * Regras de negócio inalteradas: só o nome é editável (persistido em
 * user_metadata.full_name); e-mail é somente leitura; a foto é local (base64)
 * via AppContext.updateAvatar.
 */
export default function MeuPerfil() {
  const navigate = useNavigate()
  const { user, toast, avatarUrl, updateAvatar, logout, confirm } = useApp()
  const { updateUserMetadata, resetPassword } = useAuth()

  const fileRef = useRef(null)
  const [name, setName] = useState(user?.name || '')
  const [savingName, setSavingName] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [sendingReset, setSendingReset] = useState(false)

  // Mantém o input sincronizado quando o nome é atualizado externamente
  // (ex.: evento USER_UPDATED após salvar).
  useEffect(() => {
    setName(user?.name || '')
  }, [user?.name])

  function handlePhotoClick() {
    fileRef.current?.click()
  }

  function handlePhotoChange(e) {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast('Selecione um arquivo de imagem.', 'error')
      return
    }
    const MAX_BYTES = 2 * 1024 * 1024
    if (file.size > MAX_BYTES) {
      toast('Imagem muito grande (máx. 2MB).', 'error')
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      updateAvatar(reader.result)
      toast('Foto de perfil atualizada.', 'success')
    }
    reader.onerror = () => toast('Falha ao ler a imagem.', 'error')
    reader.readAsDataURL(file)
    e.target.value = '' // permite re-selecionar o mesmo arquivo
  }

  async function handleSaveName() {
    const trimmed = name.trim()
    if (!trimmed) {
      toast('Nome não pode ficar vazio.', 'error')
      return
    }
    try {
      setSavingName(true)
      const { error } = await updateUserMetadata({ full_name: trimmed })
      if (error) throw error
      toast('Nome atualizado com sucesso.', 'success')
    } catch (err) {
      toast(err.message || 'Falha ao atualizar o nome.', 'error')
    } finally {
      setSavingName(false)
    }
  }

  async function handleChangePassword() {
    if (!user?.email) return
    try {
      setSendingReset(true)
      const { error } = await resetPassword(user.email)
      if (error) throw error
      toast('Enviamos um e-mail com o link para você redefinir sua senha.', 'success')
    } catch (err) {
      toast(err.message || 'Falha ao enviar o e-mail de redefinição.', 'error')
    } finally {
      setSendingReset(false)
    }
  }

  async function handleLogout() {
    try {
      setLoggingOut(true)
      await logout()
    } finally {
      navigate('/login', { replace: true })
    }
  }

  async function handleDelete() {
    const ok = await confirm({
      title: 'Excluir conta',
      message:
        'Isso remove permanentemente a sua conta e o seu acesso. Os dados da ' +
        'organização são preservados, mas você perderá o acesso a eles. Esta ' +
        'ação é irreversível.',
      danger: true,
      confirmText: 'Excluir minha conta',
    })
    if (!ok) return
    try {
      setDeleting(true)
      await api.deleteAccount()
      // Conta removida no backend — encerra a sessão local e volta ao login.
      await logout()
      navigate('/login', { replace: true })
    } catch (err) {
      setDeleting(false)
      toast(err.message || 'Falha ao excluir a conta.', 'error')
    }
  }

  const nameUnchanged = name.trim() === (user?.name || '').trim()

  return (
    <div className="mp-page">
      <header className="mp-card mp-identity">
        <button type="button" className="mp-avatar" onClick={handlePhotoClick} aria-label="Alterar foto de perfil">
          {avatarUrl ? <img src={avatarUrl} alt="" /> : <span>{user?.avatar || 'U'}</span>}
          <span className="mp-avatar-edit"><Icon n="upload" s={12} /></span>
        </button>
        <input ref={fileRef} type="file" accept="image/*" onChange={handlePhotoChange} hidden />
        <div className="mp-identity-text">
          <span className="mp-eyebrow">Meu perfil</span>
          <h1>{user?.name || 'Seu perfil'}</h1>
          <p>{user?.email}</p>
        </div>
        <div className="mp-photo-action">
          <button type="button" className="mp-button" onClick={handlePhotoClick}><Icon n="upload" s={14} />Alterar foto</button>
          <span>Imagem de até 2 MB</span>
        </div>
      </header>

      <div className="mp-details-grid">
        <section className="mp-card mp-personal" aria-labelledby="mp-personal-title">
          <div className="mp-section-heading">
            <span className="mp-section-icon"><Icon n="user" s={17} /></span>
            <div><h2 id="mp-personal-title">Dados pessoais</h2><p>Suas informações de identificação no sistema.</p></div>
          </div>
          <form onSubmit={event => { event.preventDefault(); if (!savingName && !nameUnchanged) handleSaveName() }}>
            <div className="mp-form-fields">
              <div className="mp-field">
                <label htmlFor="mp-name">Nome completo</label>
                <input id="mp-name" autoComplete="name" value={name} onChange={event => setName(event.target.value)} placeholder="Seu nome" disabled={savingName} />
              </div>
              <div className="mp-field">
                <label htmlFor="mp-email">E-mail <span>Somente leitura</span></label>
                <input id="mp-email" type="text" value={user?.email || ''} readOnly />
              </div>
            </div>
            <div className="mp-form-footer">
              <button type="submit" className="mp-button mp-button-primary" disabled={savingName || nameUnchanged}>
                <Icon n="check" s={14} />{savingName ? 'Salvando…' : 'Salvar alterações'}
              </button>
            </div>
          </form>
        </section>
        <ProfileAccountDetails />
      </div>

      <section className="mp-card mp-security" aria-labelledby="mp-security-title">
        <div className="mp-section-heading">
          <span className="mp-section-icon"><Icon n="key" s={17} /></span>
          <div><h2 id="mp-security-title">Segurança e acesso</h2><p>Gerencie sua senha e a sessão atual.</p></div>
        </div>
        <div className="mp-setting-row">
          <div><h3>Senha de acesso</h3><p>Receba por e-mail um link para redefinir sua senha.</p></div>
          <button type="button" className="mp-button" onClick={handleChangePassword} disabled={sendingReset}>
            {sendingReset ? 'Enviando…' : 'Alterar senha'}<Icon n="chevron_r" s={14} />
          </button>
        </div>
        <div className="mp-setting-row">
          <div><h3>Sessão atual</h3><p>Encerre seu acesso neste navegador.</p></div>
          <button type="button" className="mp-button" onClick={handleLogout} disabled={loggingOut}>
            <Icon n="export" s={14} />{loggingOut ? 'Saindo…' : 'Sair da conta'}
          </button>
        </div>
      </section>

      <section className="mp-danger" aria-labelledby="mp-delete-title">
        <div><h2 id="mp-delete-title">Excluir conta</h2><p>Remove permanentemente seu perfil e acesso ao sistema.</p></div>
        <button type="button" className="mp-button mp-button-danger" onClick={handleDelete} disabled={deleting}>
          <Icon n="trash" s={14} />{deleting ? 'Excluindo…' : 'Excluir minha conta'}
        </button>
      </section>
    </div>
  )
}
